<admin-header [primaryButtons]="headerPrimaryButtons" [tertiaryButtons]="headerTertiaryButtons" [title]="title" (eventFired)="headerButtonClick($event)"></admin-header>

<div class="planner">
	<!-- Toolbar: the board at a glance, then the filters -->
	<div class="toolbar">
		<div class="stats">
			<div class="stat stat-running"><i class="fal fa-play-circle"></i><b>{{ runningCount }}</b><span>Running</span></div>
			<div class="stat stat-queued"><i class="fal fa-layer-group"></i><b>{{ queuedCount }}</b><span>Queued</span></div>
			<div class="stat stat-paused"><i class="fal fa-pause-circle"></i><b>{{ pausedCount }}</b><span>Paused</span></div>
			<div class="stat stat-unassigned"><i class="fal fa-question-circle"></i><b>{{ unassignedCount }}</b><span>No Machine</span></div>
		</div>
		<div class="filters">
			<button class="icon-btn" (click)="getData()" title="Refresh"><i class="fal fa-sync-alt"></i></button>
			<button class="pill-btn" (click)="viewToday()">Today</button>
			<dx-date-box *ngIf="useDate" [(value)]="opsPlanningDate" displayFormat="dd/MM/yyyy" type="date" (onValueChanged)="changeDate($event)" [width]="150"></dx-date-box>
			<dx-check-box [value]="useDate" text="Filter by date" (onValueChanged)="toggleDate()"></dx-check-box>
			<span class="view-toggles">
				<button [class.active]="currentView === 'machine'" (click)="changeView('machine')"><i class="fal fa-cogs"></i> Machine</button>
				<button [class.active]="currentView === 'productionTool'" (click)="changeView('productionTool')"><i class="fal fa-wrench"></i> Tool</button>
				<button [class.active]="currentView === 'operator'" (click)="changeView('operator')"><i class="fal fa-user-hard-hat"></i> Operator</button>
			</span>
		</div>
	</div>

	<toolroom-loader *ngIf="isLoading" text="Loading the planner" variant="draw"></toolroom-loader>

	<div class="board">
		<div *ngFor="let group of grouped" class="lane"
			[class.lane-running]="group.runningStages.length > 0"
			[class.lane-queued]="group.runningStages.length === 0 && group.availableStages.length > 0"
			[class.lane-unassigned]="group.groupId === 0"
			[class.drag-over]="dragOverGroupId === group.groupId && draggedFromGroupId !== group.groupId"
			(dragover)="onDragOverGroup($event, group.groupId)" (dragleave)="onDragLeaveGroup(group.groupId)" (drop)="$event.preventDefault(); onDropOnGroup(group.groupId)">

			<div class="lane-head">
				<div class="lane-title">
					<span class="lane-icon">
						<i class="fal" [class.fa-cogs]="currentView === 'machine'" [class.fa-wrench]="currentView === 'productionTool'" [class.fa-user-hard-hat]="currentView === 'operator'"></i>
					</span>
					<h3>{{ group.groupName }}</h3>
				</div>
				<div class="lane-counts">
					<!-- The machine's own status, not the work's. Clickable for a supervisor, read-only for everyone else. -->
					<button *ngIf="group.status && canChangeMachineStatus" type="button" class="lane-status" [ngClass]="'tone-' + group.status.tone"
						(click)="changeMachineStatusPopup(group.machine)" title="Change this machine's production status"><i></i>{{ group.status.label }}</button>
					<span *ngIf="group.status && canChangeMachineStatus === false" class="lane-status" [ngClass]="'tone-' + group.status.tone"><i></i>{{ group.status.label }}</span>
					<span class="count count-running" *ngIf="group.runningStages.length > 0"><i class="fas fa-circle"></i> {{ group.runningStages.length }} running</span>
					<span class="count count-queued">{{ group.availableStages.length }} queued</span>
				</div>
			</div>

			<!-- Running -->
			<div *ngIf="group.runningStages.length > 0" class="section">
				<div class="section-label label-running"><i class="fal fa-play"></i> In Progress</div>
				<div *ngFor="let stage of group.runningStages" class="job job-running" [class.job-paused]="stage.isPaused">
					<ng-container *ngTemplateOutlet="jobCard; context: { stage: stage, queued: false }"></ng-container>
				</div>
			</div>

			<!-- Queue -->
			<div class="section">
				<div class="section-label label-queued"><i class="fal fa-list-ol"></i> Queue</div>
				<div *ngFor="let stage of group.availableStages" class="job job-queued" draggable="true"
					[class.drag-target]="dragOverStageId === stage.id"
					(dragstart)="onDragStart($event, stage.id, group.groupId)"
					(dragend)="onDragEnd()"
					(dragover)="onDragOverStage($event, stage.id)"
					(drop)="$event.stopPropagation(); $event.preventDefault(); onDropOnStage(stage.id, group.groupId)">
					<ng-container *ngTemplateOutlet="jobCard; context: { stage: stage, queued: true }"></ng-container>
				</div>
				<div *ngIf="group.availableStages.length === 0" class="empty"><i class="fal fa-inbox"></i> Nothing queued</div>
			</div>
		</div>
	</div>
</div>

<!-- One job: the billet's picture, what it is, who and when, and what can be done with it -->
<ng-template #jobCard let-stage="stage" let-queued="queued">
	<div class="job-thumb" (click)="goToToolOrder(stage.toolOrderId)" title="Open billet">
		<img *ngIf="stage.toolOrder?.imageUrl" [src]="stage.toolOrder.imageUrl" alt="Billet" />
		<i *ngIf="!stage.toolOrder?.imageUrl" class="fal fa-cube"></i>
		<span *ngIf="queued" class="priority" title="Priority">{{ stage.priority }}</span>
	</div>
	<div class="job-body">
		<div class="job-top">
			<a class="job-billet" (click)="goToToolOrder(stage.toolOrderId)">{{ stage.toolOrder?.name || ('Billet ' + stage.toolOrderId) }}</a>
			<span class="paused-tag" *ngIf="stage.isPaused"><i class="fal fa-pause"></i> Paused</span>
		</div>
		<span class="op-chip" [style.background]="processColour(stage.processDescription).background" [style.color]="processColour(stage.processDescription).color">
			OP{{ (stage.stageNumber || 0) * 10 }} &middot; {{ stage.processDescription }}
		</span>
		<div class="job-tool" *ngIf="stage.toolOrder?.productionTool"><i class="fal fa-wrench"></i> {{ stage.toolOrder.productionTool.name }}</div>
		<div class="job-meta">
			<span *ngIf="stage.partNumbers" class="meta meta-part"><i class="fal fa-cube"></i> {{ stage.partNumbers }}</span>
			<span *ngIf="stage.programName" class="meta meta-program"><i class="fal fa-file-code"></i> {{ stage.programName }}</span>
			<span *ngIf="queued && stage.estimatedStartDate" class="meta meta-date"><i class="fal fa-calendar"></i> {{ stage.estimatedStartDate | date:'dd/MM' }}</span>
		</div>
		<div *ngIf="!queued && progressByOperation[stage.id] as p" class="job-progress" [class.paused]="stage.isPaused" [class.over]="p.overrun">
			<div class="jp-top">
				<b>{{ stage.isPaused ? 'Paused' : p.left + (p.overrun ? ' over' : ' left') }}</b>
				<span>finish {{ p.finishAt | date: 'EEE HH:mm' }}</span>
			</div>
			<div class="jp-bar">
				<i *ngFor="let seg of p.segments" class="jp-seg" [class.now]="seg.index === p.currentIndex" [style.left.%]="seg.startPercent" [style.width.%]="seg.widthPercent"></i>
				<div class="jp-fill" [style.width.%]="p.percent"></div>
			</div>
			<div *ngIf="p.cutterLabel" class="jp-cutter"><i class="fal fa-cog"></i> Should be cutting <b>{{ p.cutterLabel }}</b> <span>{{ p.cutterPosition }}</span></div>
		</div>
		<div class="job-foot">
			<span class="operator" *ngIf="stage.assignedUserProfile">
				<span class="avatar">{{ initials(stage.assignedUserProfile) }}</span>
				{{ stage.assignedUserProfile.firstName }} {{ stage.assignedUserProfile.lastName }}
			</span>
			<span class="operator none" *ngIf="!stage.assignedUserProfile"><span class="avatar">?</span> No operator</span>
			<div class="job-actions">
				<button *ngIf="queued" class="act act-start" (click)="startStage(stage.id)" title="Start"><i class="fas fa-play"></i></button>
				<button *ngIf="!queued" class="act act-complete" (click)="completeStage(stage.id)" title="Complete"><i class="fas fa-check"></i></button>
				<button *ngIf="!queued" class="act act-pause" (click)="togglePause(stage.id)" [title]="stage.isPaused ? 'Resume' : 'Pause'"><i class="fas" [class.fa-pause]="!stage.isPaused" [class.fa-play]="stage.isPaused"></i></button>
				<button class="act act-machine" (click)="changeMachinePopup(stage.id, stage.machineId)" title="Change Machine"><i class="fal fa-cogs"></i></button>
				<button class="act act-operator" (click)="changeOperatorPopup(stage.id, stage.assignedUserProfileId)" title="Change Operator"><i class="fal fa-user-edit"></i></button>
				<button class="act act-time" (click)="changeEstimatesPopup(stage.id, stage)" title="Estimates"><i class="fal fa-clock"></i></button>
				<button class="act act-program" (click)="openProgram(stage)" title="Program sheet - print or save as PDF"><i class="fal fa-file-download"></i></button>
			</div>
		</div>
	</div>
</ng-template>

<!-- Popup -->
<dx-popup [width]="400" [height]="popupTitle === 'Change Estimates' ? 400 : 250" [showTitle]="true" [title]="popupTitle" [dragEnabled]="false" [closeOnOutsideClick]="true" [showCloseButton]="true" container=".dx-viewport" [(visible)]="popupVisible">
	<dxi-toolbar-item widget="dxButton" toolbar="bottom" location="before" [options]="popupSaveButtonOptions"></dxi-toolbar-item>
	<dxi-toolbar-item widget="dxButton" toolbar="bottom" location="after" [options]="popupCloseButtonOptions"></dxi-toolbar-item>
	<div class="m-3">
		<div *ngIf="popupTitle === 'Change Machine'">
			<label>Machine</label>
			<dx-select-box [(value)]="popupMachineId" [items]="machines" displayExpr="cellNumber" valueExpr="id" [searchEnabled]="true" [showClearButton]="true"></dx-select-box>
		</div>
		<div *ngIf="popupTitle === 'Change Operator'">
			<label>Operator</label>
			<dx-select-box [(value)]="popupOperatorId" [items]="operators" displayExpr="firstName" valueExpr="id" [searchEnabled]="true" [showClearButton]="true"></dx-select-box>
		</div>
		<div *ngIf="popupTitle === 'Change Production Status'">
			<label>Production Status</label>
			<dx-select-box [(value)]="popupMachineStatus" [items]="machineStatuses" displayExpr="name" valueExpr="id" [searchEnabled]="true"></dx-select-box>
		</div>
		<div *ngIf="popupTitle === 'Change Estimates'">
			<div style="margin-bottom:12px">
				<label>Estimated Start Date</label>
				<dx-date-box [(value)]="popupEstimatedStartDate" displayFormat="dd/MM/yyyy" type="date"></dx-date-box>
			</div>
			<div style="margin-bottom:12px">
				<label>Estimated Setup Time (hrs)</label>
				<dx-number-box [(value)]="popupEstimatedSetupTime" [format]="{type:'fixedPoint', precision: 2}" [min]="0"></dx-number-box>
			</div>
			<div>
				<label>Estimated Run Time (hrs)</label>
				<dx-number-box [(value)]="popupEstimatedRunTime" [format]="{type:'fixedPoint', precision: 2}" [min]="0"></dx-number-box>
			</div>
		</div>
	</div>
</dx-popup>
